<template>
  <div class="toolbar-option">
    <!-- Color picker -->
    <v-menu
      v-if="option.action === 'colorPicker'"
      v-model="colorPickerOpen"
      :close-on-content-click="false"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          :data-testid="`${option.icon}-button`"
          class="toolbarOption"
          :class="{'selectedToolbarOption' : colorPickerOpen}"
          @click="$emit('color-picker-click', option)"
        >
          <component
            :is="option.icon + 'Icon'"
            class="toolbarIcon"
          />
          <div
            :style="handleStyle(option)"
            class="colorSwatch"
          />
        </button>
      </template>
      <div
        v-if="openPicker === option.name"
        class="pickerContainer"
      >
        <ColorPicker
          v-for="(colorPicker, colorPickerIndex) in colorPickerData"
          :key="colorPickerIndex"
          :color-picker-data="colorPicker"
          @colorChange="$forceUpdate() /* Color Picker icon swatches do not update on reactivity alone.*/"
        />
        <div
          v-if="option.slider"
          class="sliderSection"
          @keydown.enter.stop.prevent
        >
          <span class="sliderLabel">{{ option.slider.label.toUpperCase() }}</span>
          <div class="sliderRow">
            <v-slider
              v-model="sliderValue"
              :min="option.slider.min"
              :max="option.slider.max"
              :step="option.slider.step"
              hide-details
              :color="option.slider.getColor?.()"
              :thumb-color="option.slider.getColor?.()"
              class="sliderInput"
              @update:model-value="option.slider.setValue($event)"
            />
            <div class="sliderValueInput">
              <input
                :value="sliderValue"
                type="number"
                :min="option.slider.min"
                :max="option.slider.max"
                class="sliderNumberInput"
                @change="handleSliderInput($event)"
                @keydown.enter.stop.prevent="handleSliderInput($event)"
              ><span class="sliderPercent">%</span>
            </div>
          </div>
        </div>
      </div>
    </v-menu>
    <!-- Icon picker -->
    <v-menu
      v-else-if="option.action === 'iconPicker'"
      v-model="iconPickerOpen"
      :close-on-content-click="false"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          :data-testid="`${option.icon}-button`"
          class="toolbarOption"
          :class="{'selectedToolbarOption': iconPickerOpen}"
          @click="$emit('icon-picker-click', option)"
        >
          <component
            :is="option.icon + 'Icon'"
            class="toolbarIcon"
          />
        </button>
      </template>
      <!-- Icon Picker Popup -->
      <div
        v-if="openPicker === option.name"
        ref="iconPickersDiv"
        class="pickerContainer"
      >
        <div>
          <IconPicker
            v-for="(iconPicker, iconPickerIndex) in iconPickerData"
            :key="iconPickerIndex"
            class="iconPicker"
            :icon-picker-data="iconPicker"
            @colorChange="$forceUpdate() /* Color Picker icon swatches do not update on reactivity alone. */"
          />
        </div>
      </div>
    </v-menu>
    <!-- Alignment dropdown -->
    <v-menu
      v-else-if="option.action === 'openAlignmentDropdown'"
      v-model="alignmentDropdownOpen"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          :data-testid="`${option.icon}-button`"
          class="toolbarOption dropdownButton"
          :class="{expanded: alignmentDropdownOpen}"
          @click="$emit('alignment-dropdown-click', option)"
        >
          <component
            :is="option.icon + 'Icon'"
            class="toolbarIcon"
          />
          <CaretIcon class="caret" />
        </button>
      </template>
      <!-- Alignment Dropdown -->
      <div
        v-if="alignmentDropdownOpen"
        class="options-dropdown dropdownContents"
        data-testid="alignment-dropdown"
      >
        <button
          v-for="(dropdownOption, dropdownIndex) in alignmentPickerData"
          :key="dropdownIndex"
          class="toolbarOption"
          :class="{'selectedToolbarOption': dropdownOption.isSelected?.()}"
          @click="$emit('alignment-dropdown-option-click', dropdownOption.callback)"
        >
          <component
            :is="dropdownOption.icon + 'Icon'"
            class="toolbarIcon"
          />
        </button>
      </div>
    </v-menu>
    <!-- Default options -->
    <np-action-btn
      v-else-if="option.icon === 'Text'"
      :variant="option.variant"
      :disabled="option.disabled ?? false"
      :loading="option.loading ?? false"
      size="small"
      class="ml-3"
      :aria-label="option.name"
      @click="$emit('toolbar-option-click', option)"
    >
      {{ option.name }}
    </np-action-btn>
    <button
      v-else
      v-ripple
      :data-testid="`${option.icon}-button`"
      class="toolbarOption"
      :class="{'selectedToolbarOption': option.isSelected?.()}"
      :aria-label="option.name"
      @click="$emit('toolbar-option-click', option)"
    >
      <component
        :is="option.icon + 'Icon'"
        :class="getToolbarIconClass(option.icon)"
        v-bind="getIconProps(option.icon)"
      />
    </button>
  </div>
</template>

<script lang="ts">
import ColorPicker from '@/components/InfoWindow/sections/Toolbar/ColorPicker.vue';
import BoldIcon from '@/components/svgs/ToolbarIcons/BoldIcon.vue';
import CenterAlignIcon from '@/components/svgs/ToolbarIcons/CenterAlignIcon.vue';
import FillIcon from '@/components/svgs/ToolbarIcons/FillIcon.vue';
import FontIcon from '@/components/svgs/ToolbarIcons/FontIcon.vue';
import ItalicIcon from '@/components/svgs/ToolbarIcons/ItalicIcon.vue';
import LeftAlignIcon from '@/components/svgs/ToolbarIcons/LeftAlignIcon.vue';
import PencilIcon from '@/components/svgs/ToolbarIcons/PencilIcon.vue';
import TableIcon from '@/components/svgs/ToolbarIcons/TableIcon.vue';
import RightAlignIcon from '@/components/svgs/ToolbarIcons/RightAlignIcon.vue';
import UnderlineIcon from '@/components/svgs/ToolbarIcons/UnderlineIcon.vue';
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import TrashIcon from '@/components/svgs/TrashIcon.vue';
import CheckIcon from '@/components/svgs/CheckIcon.vue';
import ContentCopyIcon from '@/components/svgs/ToolbarIcons/ContentCopyIcon.vue';
import ExcelIcon from '@/components/svgs/ExcelIcon.vue';
import AddLinkIcon from '@/components/svgs/ToolbarIcons/AddLinkIcon.vue';
import LabelIcon from '@/components/svgs/LabelIcon.vue';
import AuditTrailIcon from '@/components/svgs/AuditTrailIcon.vue';
import UnderwritingDashboardIcon from '@/components/svgs/UnderwritingDashboardIcon.vue';
import IconPicker from '@/components/InfoWindow/sections/Toolbar/IconPicker.vue';
import {AlignmentOptions, ColorPickerOptions, IconPickerOptions, ToolbarOptions} from '@/services/GraphicService';
import type {PickerItem} from './types';

export default {
  name: 'ToolbarOption',
  components: {
    BoldIcon,
    CenterAlignIcon,
    FillIcon,
    FontIcon,
    ItalicIcon,
    LeftAlignIcon,
    PencilIcon,
    TableIcon,
    RightAlignIcon,
    UnderlineIcon,
    CaretIcon,
    IconPicker,
    ColorPicker,
    TrashIcon,
    CheckIcon,
    ContentCopyIcon,
    ExcelIcon,
    AddLinkIcon,
    LabelIcon,
    AuditTrailIcon,
    UnderwritingDashboardIcon,
  },
  props: {
    option: {
      type: Object as () => ToolbarOptions,
      required: true,
    },
    openPicker: {
      type: [String, null],
      required: true,
    },
    pickerData: {
      type: Array as () => PickerItem[],
      required: true,
    },
  },
  emits: [
    'icon-picker-click',
    'color-picker-click',
    'toolbar-option-click',
    'alignment-dropdown-option-click',
    'alignment-dropdown-click',
  ],
  data() {
    return {
      colorPickerOpen: false,
      iconPickerOpen: false,
      alignmentDropdownOpen: false,
      sliderValue: this.option.slider?.getValue() ?? 0,
    };
  },
  computed: {
    colorPickerData(): ColorPickerOptions[] {
      return this.option.action === 'colorPicker' ? this.pickerData as ColorPickerOptions[] : [];
    },
    iconPickerData(): IconPickerOptions[] {
      return this.option.action === 'iconPicker' ? this.pickerData as IconPickerOptions[] : [];
    },
    alignmentPickerData(): AlignmentOptions[] {
      return this.option.action === 'openAlignmentDropdown' ? this.pickerData as AlignmentOptions[] : [];
    },
  },
  watch: {
    colorPickerOpen(isOpen: boolean) {
      if (isOpen && this.option.slider) {
        this.sliderValue = this.option.slider.getValue();
      }
    },
  },
  methods: {
    handleSliderInput(event: Event) {
      const input = event.target as HTMLInputElement;
      const {min, max} = this.option.slider;
      const clamped = Math.min(max, Math.max(min, Number(input.value)));
      this.sliderValue = clamped;
      this.option.slider.setValue(clamped);
    },
    getToolbarIconClass(icon: string) {
      if (icon === 'Check' || icon === 'Table') {
        return '';
      }
      // LabelIcon draws its own frame, so its glyph needs a larger box to match the other icons
      if (icon === 'Label') {
        return 'toolbarIcon labelToolbarIcon';
      }
      return 'toolbarIcon';
    },
    getIconProps(icon: string) {
      if (icon === 'Excel') {
        return {showText: false};
      }
      return {};
    },
    handleStyle(option: ToolbarOptions) {
      const fillColorPicker = option.pickers.length > 1 ? 1 : 0;
      return {
        border: `1px solid ${'getColor' in option.pickers[0] ? option.pickers[0].getColor() : ''}`,
        backgroundColor: 'getColor' in option.pickers[fillColorPicker] ? option.pickers[fillColorPicker].getColor() : '',
      };
    },
  },
};
</script>

<!-- eslint-disable vue-scoped-css/no-parsing-error -->
<style lang="scss" scoped>
.pickerContainer {
  filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.4));
  z-index: 2;
  background-color: white;
  border-radius: 5px;
  padding: 1px 10px;
}

.sliderSection {
  border-top: 1px solid main.$lightGray;
  padding: 4px 0 8px;
}

.sliderLabel {
  font-size: 12px;
  font-weight: 600;
  color: main.$npGray;
  margin: 4px;
}

.sliderRow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sliderInput {
  flex: 1;
}

.sliderValueInput {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.sliderNumberInput {
  width: 32px;
  font-size: 12px;
  color: main.$npGray;
  text-align: right;
  border: 1px solid main.$lightGray;
  border-radius: 3px;
  padding: 2px 4px;
  outline: none;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus {
    border-color: #e18d3d;
  }
}

.sliderPercent {
  font-size: 12px;
  color: main.$npGray;
  margin-left: 1px;
}

.optionSection {
  display: flex;
  flex-direction: row;
  padding-left: 4px;
}

.divider {
  height: 20px;
  border-right: 1px solid lightgray;
  margin-left: 4px;
  align-self: center;
}

.optionDiv {
  display: flex;
  justify-content: center;
  align-items: center;
}

.toolbarOption {
  width: 30px;
  height: 30px;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  padding: 2px;
  border-radius: 4px;
  &:hover {
    background-color: main.$lightGray;
  }
  &.dropdownButton {
    flex-direction: row;
    align-items: center;
    width: 50px;
    .caret {
      width: 15px;
    }
    &.expanded {
      .caret {
        transform: rotate(180deg) scale(1.5);
      }
    }
  }
}

.selectedToolbarOption {
  background-color: main.$lightGray;
}

.toolbarIcon {
  width: 16px;
  height: 16px;
  :deep(path) {
    fill: main.$npGray;
  }
}

.labelToolbarIcon {
  width: 20px;
  height: 20px;
}

.alignmentIcon {
  transform: scaleY(-1);
}

.colorSwatch {
  width: 20px;
  height: 3px;
}

.dropdown-container {
  z-index: 999;
}

.options-dropdown {
  top: 100%;
  background-color: white;
  box-shadow: 0px 1px 6px #00000033;
}

.dropdownContents {
  display: flex;
  flex-direction: row;
  padding: 2px;
  border-radius: 5px;

  &.vertical {
    flex-direction: column;
  }
}
</style>
